/**
 * Promo kampanje - modul na probu radi marketinga.
 *
 * Ekran postoji zbog **brojeva**: koliko je dato, koliko je konvertovano u
 * plaćeno, koliko je isteklo. Bez toga se ne zna da li je kampanja radila, a to
 * joj je jedina svrha - da se meri samo koliko je dato, izgledala bi uspešno i
 * kad niko nije ostao.
 *
 * Dodela nije nov pojam: to je red u knjizi licenci sa `billing_cycle='trial'`.
 * Proba se **nikad ne pretvara sama u plaćenu**.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Gift, Plus, RefreshCw, X } from 'lucide-react';
import { api } from '../../api/client';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type Stats = { granted: number; active: number; converted: number; expired: number; conversionRate: number | null };

type Kampanja = {
  id: string;
  name: string;
  moduleCodes: string[];
  trialDays: number;
  audienceNote: string;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
  createdAt: string;
  stats: Stats;
};

type Modul = { moduleCode: string; name: string };
type Klijent = { id: string; name: string; taxId?: string };

export function PromoCampaignsPage() {
  const [kampanje, setKampanje] = useState<Kampanja[]>([]);
  const [moduli, setModuli] = useState<Modul[]>([]);
  const [klijenti, setKlijenti] = useState<Klijent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [poruka, setPoruka] = useState<string | null>(null);
  const [nova, setNova] = useState(false);
  const [dodela, setDodela] = useState<Kampanja | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [k, m, t] = await Promise.all([
        api.get<Kampanja[]>('/admin/promo-campaigns'),
        api.get<any[]>('/admin/module-prices'),
        api.get<any>('/admin/tenants'),
      ]);
      setKampanje(k);
      setModuli(m.map((x) => ({ moduleCode: x.moduleCode, name: x.name })));
      setKlijenti(Array.isArray(t) ? t : (t.rows ?? []));
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const ugasi = async (id: string) => {
    try {
      await api.post(`/admin/promo-campaigns/${id}/deactivate`, {});
      setPoruka('Kampanja ugašena - već dodeljene probe traju do svog roka.');
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    }
  };

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Promo kampanje</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Modul na probu radi marketinga · proba se <strong>nikad ne pretvara sama u plaćenu</strong>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => void refresh()} disabled={loading} style={btnSecondary}><RefreshCw size={13} /> Osveži</button>
        <button onClick={() => setNova(true)} style={btnPrimary} data-testid="nova-kampanja"><Plus size={13} /> Nova kampanja</button>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {poruka && <div style={okBox}><Check size={14} /> {poruka}</div>}

      {kampanje.length === 0 && !loading && (
        <div style={{ ...card, padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>
          <Gift size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
          <div>Još nema nijedne kampanje.</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {kampanje.map((k) => (
          <div key={k.id} style={{ ...card, padding: 16 }} data-testid={`kampanja-${k.id}`}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, color: 'var(--color-text-strong)', fontSize: 15 }}>{k.name}</span>
                  <span style={oznaka(k.isActive)}>{k.isActive ? 'aktivna' : 'ugašena'}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                  {k.moduleCodes.join(', ')} · proba {k.trialDays} dana
                  {k.audienceNote && ` · ${k.audienceNote}`}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 18 }}>
                <Broj oznaka="dato" vrednost={k.stats.granted} />
                <Broj oznaka="u toku" vrednost={k.stats.active} />
                <Broj oznaka="konvertovano" vrednost={k.stats.converted} istaknuto />
                <Broj oznaka="isteklo" vrednost={k.stats.expired} />
                <Broj oznaka="konverzija" vrednost={k.stats.conversionRate == null ? '-' : `${k.stats.conversionRate} %`} istaknuto />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                {k.isActive && (
                  <>
                    <button onClick={() => setDodela(k)} style={btnPrimary} data-testid={`dodeli-${k.id}`}>Dodeli klijentu</button>
                    <button onClick={() => void ugasi(k.id)} style={btnSecondary}>Ugasi</button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {nova && (
        <NovaKampanja
          moduli={moduli}
          onZatvori={() => setNova(false)}
          onGotovo={() => { setNova(false); setPoruka('Kampanja napravljena.'); void refresh(); }}
          onGreska={setError}
        />
      )}

      {dodela && (
        <Dodela
          kampanja={dodela}
          klijenti={klijenti}
          onZatvori={() => setDodela(null)}
          onGotovo={(p) => { setDodela(null); setPoruka(p); void refresh(); }}
          onGreska={setError}
        />
      )}
    </div>
  );
}

function Broj({ oznaka: o, vrednost, istaknuto }: { oznaka: string; vrednost: number | string; istaknuto?: boolean }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 74 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: istaknuto ? 'var(--color-status-paid-text, #86efac)' : 'var(--color-text-strong)' }}>{vrednost}</div>
      <div style={{ fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{o}</div>
    </div>
  );
}

function NovaKampanja({ moduli, onZatvori, onGotovo, onGreska }: {
  moduli: Modul[]; onZatvori: () => void; onGotovo: () => void; onGreska: (e: string) => void;
}) {
  const [name, setName] = useState('');
  const [izabrani, setIzabrani] = useState<Set<string>>(new Set());
  const [trialDays, setTrialDays] = useState('30');
  const [audienceNote, setAudienceNote] = useState('');
  const [radi, setRadi] = useState(false);

  const toggle = (code: string) => {
    const n = new Set(izabrani);
    n.has(code) ? n.delete(code) : n.add(code);
    setIzabrani(n);
  };

  const sacuvaj = async () => {
    setRadi(true);
    try {
      await api.post('/admin/promo-campaigns', {
        name, moduleCodes: [...izabrani], trialDays: Number(trialDays) || 30, audienceNote,
      });
      onGotovo();
    } catch (e: any) {
      onGreska(e?.data?.message || e?.message || 'Kampanja nije napravljena');
    } finally {
      setRadi(false);
    }
  };

  return (
    <Dijalog naslov="Nova promo kampanja" onZatvori={onZatvori}>
      <Polje label="Naziv">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="npr. Avgustovska akcija" style={input} data-testid="kampanja-naziv" />
      </Polje>

      <Polje label="Moduli koje kampanja poklanja">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {moduli.map((m) => (
            <button
              key={m.moduleCode}
              onClick={() => toggle(m.moduleCode)}
              style={{
                ...btnSecondary, padding: '5px 10px', fontSize: 12,
                background: izabrani.has(m.moduleCode) ? 'rgba(59,130,246,0.2)' : 'var(--color-bg)',
                borderColor: izabrani.has(m.moduleCode) ? '#60a5fa' : 'var(--color-border)',
              }}
            >
              {m.name}
            </button>
          ))}
        </div>
      </Polje>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
        <Polje label="Trajanje probe (dana)">
          <input value={trialDays} onChange={(e) => setTrialDays(e.target.value)} style={input} />
        </Polje>
        <Polje label="Ko je dobija (beleška)">
          <input value={audienceNote} onChange={(e) => setAudienceNote(e.target.value)} placeholder="npr. svi sa POS Basic" style={input} />
        </Polje>
      </div>

      <div style={{ ...napomena, marginTop: 12 }}>
        Klijent dobija obaveštenje odmah, pa podsetnik <strong>7 i 1 dan pre isteka</strong>. Proba se ne obnavlja
        sama - ako je želi zadržati, mora sam da je prevede u plaćenu.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
        <button onClick={onZatvori} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
        <button onClick={() => void sacuvaj()} disabled={radi || !name.trim() || izabrani.size === 0} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: radi || !name.trim() || izabrani.size === 0 ? 0.5 : 1 }} data-testid="kampanja-sacuvaj">
          {radi ? 'Čuvanje...' : 'Sačuvaj'}
        </button>
      </div>
    </Dijalog>
  );
}

function Dodela({ kampanja, klijenti, onZatvori, onGotovo, onGreska }: {
  kampanja: Kampanja; klijenti: Klijent[];
  onZatvori: () => void; onGotovo: (p: string) => void; onGreska: (e: string) => void;
}) {
  const [tenantId, setTenantId] = useState('');
  const [moduleCode, setModuleCode] = useState(kampanja.moduleCodes[0] ?? '');
  const [radi, setRadi] = useState(false);

  const dodeli = async () => {
    setRadi(true);
    try {
      const r = await api.post<{ validUntil: string }>(`/admin/promo-campaigns/${kampanja.id}/grant`, { tenantId, moduleCode });
      onGotovo(`Proba dodeljena, važi do ${r.validUntil}.`);
    } catch (e: any) {
      onGreska(e?.data?.message || e?.message || 'Dodela nije uspela');
    } finally {
      setRadi(false);
    }
  };

  return (
    <Dijalog naslov={`Dodela - ${kampanja.name}`} onZatvori={onZatvori}>
      <Polje label="Klijent">
        <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} style={input} data-testid="dodela-klijent">
          <option value="">Izaberi…</option>
          {klijenti.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      </Polje>
      <Polje label="Modul">
        <select value={moduleCode} onChange={(e) => setModuleCode(e.target.value)} style={input}>
          {kampanja.moduleCodes.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </Polje>

      <div style={napomena}>
        Isti modul se klijentu daje <strong>najviše jednom</strong> na promo - inače bi se probni meseci vrteli u
        krug. Dodatak traži aktivnu kasu.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
        <button onClick={onZatvori} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
        <button onClick={() => void dodeli()} disabled={radi || !tenantId || !moduleCode} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: radi || !tenantId ? 0.5 : 1 }} data-testid="dodela-potvrdi">
          {radi ? 'Čuvanje...' : 'Sačuvaj'}
        </button>
      </div>
    </Dijalog>
  );
}

function Polje({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

function Dijalog({ naslov, children, onZatvori }: { naslov: string; children: React.ReactNode; onZatvori: () => void }) {
  useEscapeToClose(onZatvori);
  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)' }}>{naslov}</h3>
          <button onClick={onZatvori} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer' }}><X size={16} /></button>
        </header>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

const oznaka = (aktivna: boolean): React.CSSProperties => ({
  fontSize: 10, padding: '2px 7px', borderRadius: 999, fontWeight: 600, textTransform: 'uppercase',
  background: aktivna ? 'rgba(34,197,94,0.15)' : 'var(--color-bg)',
  color: aktivna ? '#86efac' : 'var(--color-text-muted)',
});

const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)' };
const btnSecondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnPrimary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, background: 'var(--color-primary, #2563eb)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12, outline: 'none' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 14 };
const okBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#86efac', fontSize: 13, marginBottom: 14 };
const napomena: React.CSSProperties = { padding: 10, borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text-muted)', fontSize: 12, lineHeight: 1.5 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 520, background: 'var(--color-bg-surface)', borderRadius: 12, border: '1px solid var(--color-border)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--color-border)' };
